import { Component, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Route } from '../../../utils/route';
import { DraftCreationTabComponent } from './draft-creation-tab/draft-creation-tab.component';
import { DraftDeletionTabComponent } from './draft-deletion-tab/draft-deletion-tab.component';
import { DraftEditingTabComponent } from './draft-editing-tab/draft-editing-tab.component';
import { TabsComponent } from '../../../components/tabs/tabs.component';
import { DictionaryService } from '../../../services/dictionary.service';

@Component({
  selector: 'app-draft-routes',
  imports: [
    DraftCreationTabComponent,
    DraftEditingTabComponent,
    DraftDeletionTabComponent,
    TabsComponent,
  ],
  templateUrl: './draft-routes.component.html',
  styleUrl: './draft-routes.component.scss',
})
export class DraftRoutesComponent extends Route implements OnInit {
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private dictionaryService = inject(DictionaryService);

  private readonly draftTabs = [
    { key: 'Creation', param: 'creation' },
    { key: 'Editing', param: 'editing' },
    { key: 'Deletion', param: 'deletion' },
  ] as const;

  defaultTab = signal<string>(
    this.toDraftTabLabel(
      this.activatedRoute.snapshot.queryParamMap.get('draftTab') || '',
    ),
  );

  ngOnInit(): void {
    this.activatedRoute.queryParams.subscribe((params) => {
      this.defaultTab.set(this.toDraftTabLabel(params['draftTab'] || ''));
    });
  }

  onTabChange(tab: string): void {
    const draftTab = this.toDraftTabParam(tab);

    if (!draftTab) {
      return;
    }

    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { draftTab },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private toDraftTabLabel(draftTabParam: string): string {
    const match = this.draftTabs.find(({ key, param }) =>
      this.isDraftTabMatch(key, draftTabParam, param),
    );
    return match ? this.dictionaryService.get(match.key) : '';
  }

  private toDraftTabParam(
    draftTabLabel: string,
  ): 'creation' | 'editing' | 'deletion' | '' {
    return (
      this.draftTabs.find(({ key, param }) =>
        this.isDraftTabMatch(key, draftTabLabel, param),
      )?.param || ''
    );
  }

  private isDraftTabMatch(
    key: 'Creation' | 'Editing' | 'Deletion',
    value: string,
    stableValue: 'creation' | 'editing' | 'deletion',
  ): boolean {
    if (value.toLowerCase() === stableValue) {
      return true;
    }

    const translations = this.dictionaryService.dictionary[key];
    return Object.values(translations).includes(value);
  }
}
